import type React from 'react';
import type { Key } from 'react';
import type { DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonDataItem } from '../../generic-types';
import type { TemplateType } from '../../template-outlet/template-outlet.component';

/**************************************
 * Generic Types
 *************************************/

export interface DropDownResult {
  value: unknown;
  text: string;
  archived?: boolean;
  /**
   * Contains the entire data of the selected item.
   */
  originalData?: JsonDataItem;
}

export interface OptionWithOptionalProperties extends DropDownResult {
  position?: number;
  archivedOption?: boolean;
  defaultOption?: boolean;
}

export interface DropDownNoSelection {
  noSelectionValue: unknown;
}

export const DropDownSelectionType = {
  Single: 0,
  Multiple: 1,
} as const;
export type DropDownSelectionType = (typeof DropDownSelectionType)[keyof typeof DropDownSelectionType];

/**************************************
 * Drop Down Types
 *************************************/

export interface DropDownActionTemplateProps {
  allowClear?: boolean;
  isOpen: boolean;
  clearSelection: () => void;
  changeDropDownVisibility: () => void;
}

export type DropDownActionTemplateType = TemplateType<DropDownActionTemplateProps>;

export interface DropDownWrapperTemplateProps {
  selectedValue?: Array<DropDownResult>;
  selectorElement: React.JSX.Element;
  actionElement: React.JSX.Element;
  label?: string;
  dropDownFinalStyles: string;
  labelInsideDropDown: boolean;
  changeDropDownVisibility: () => void;
}

export type DropDownWrapperTemplateType = TemplateType<DropDownWrapperTemplateProps>;

export interface DropDownSelectorTemplateProps {
  selectedValue: Array<DropDownResult> | undefined;
  multipleMessageCount: number;
}

export type DropDownSelectorTemplateType = TemplateType<DropDownSelectorTemplateProps>;

/**************************************
 * Drop Down Option List Types
 *************************************/

export interface DropDownOptionListTemplateProps extends DynamicFieldDataProps {
  id: Key;
  value?: Array<DropDownResult>;
  setDefaultValue: boolean;
  isMultiSelect: boolean;
  useCheckboxForMultiSelect: boolean;
  removeOnSelect: boolean;
  isListOpen: boolean;
  onChange?: (item: DropDownResult) => void;
  optionHoverStyle?: string;
  optionSelectedStyle?: string;
  scrollElement?: Element;
}

export type DropDownOptionListTemplateType = TemplateType<DropDownOptionListTemplateProps>;

/**************************************
 * Drop Down Option Types
 *************************************/

export interface DropDownOptionTemplateProps {
  value: unknown;
  text: string;
  archived: boolean;
  isMultiSelect: boolean;
  selectedValue?: Array<DropDownResult>;
  highlightedValue?: Array<DropDownResult>;
  onItemSelected: () => void;
  onDoubleClick: () => void;
  isSelected: boolean;
  isHighlighted: boolean;
}

export type DropDownOptionTemplateType = TemplateType<DropDownOptionTemplateProps>;
